import React from "react";
import { Link } from "react-router-dom";
import { HeartIcon, PlusIcon } from "lucide-react";
import type { Product } from "../types/product";
import { useCart } from "../contexts/CartContext";
import { colorSwatches, formatPrice, shopifyImg } from "../utils/format";
interface ProductCardProps {
  product: Product;
}
export function ProductCard({
  product
}: ProductCardProps) {
  const {
    add,
    wishlist,
    toggleWish
  } = useCart();
  const wished = wishlist.includes(product.id);
  const needsChoice = Boolean(product.personalizable || product.colors && product.colors.length > 1 || product.priceFrom);
  const href = `/product/${product.id}`;
  const actionLabel = product.personalizable ? "Personaliseer" : needsChoice ? "Kies opties" : "Snel toevoegen";
  const desktopAction = "absolute inset-x-3 bottom-3 hidden h-11 items-center justify-center gap-1.5 rounded-full bg-paper/95 text-sm font-medium text-ink shadow-sm backdrop-blur transition-[opacity,transform,background-color,color] duration-200 ease-out hover:bg-ink hover:text-paper lg:flex lg:translate-y-2 lg:opacity-0 lg:group-hover:translate-y-0 lg:group-hover:opacity-100 lg:focus-visible:translate-y-0 lg:focus-visible:opacity-100";
  return <article className="group flex h-full flex-col" data-id="element-334" data-mp="3czlgrnfpzvgv">
      <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-sand" data-id="element-335" data-mp="37zfoq65jg6y9">
        <Link to={href} className="block h-full w-full" tabIndex={-1} aria-hidden="true" data-id="element-336" data-mp="37xcpqwmer3he">
          <img src={shopifyImg(product.image, 800)} alt={product.imageAlt} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-337" data-mp="8jiy1w9c20du" />
        </Link>
        {(product.badge || product.personalizable) && <span className="pointer-events-none absolute left-3 top-3 rounded-full bg-paper px-2.5 py-1 text-[11px] font-medium text-ink" data-id="element-338" data-mp="2k9v0b7qm8hhl">
            {product.badge ?? "Personaliseerbaar"}
          </span>}
        <button type="button" onClick={() => toggleWish(product.id)} aria-pressed={wished} aria-label={wished ? `${product.name} van verlanglijst verwijderen` : `${product.name} aan verlanglijst toevoegen`} className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-paper text-ink transition-transform duration-150 ease-out active:scale-90" data-id="element-339" data-mp="2grfj4k2l4jr8">
          <HeartIcon className={`h-4 w-4 transition-colors duration-150 ${wished ? "fill-gold text-gold" : ""}`} data-id="element-340" data-mp="1y0o2qyk6jt3c" />
        </button>

        {needsChoice ? <Link to={href} className={desktopAction} data-id="element-341" data-mp="1p1160q2ebbgm">
            {actionLabel}
          </Link> : <button type="button" onClick={() => add(product)} className={desktopAction} data-id="element-342" data-mp="1t1l5w1ecst6g">
            <PlusIcon className="h-4 w-4" data-id="element-343" data-mp="1f21y59pgfjuo" /> {actionLabel}
          </button>}
        {!needsChoice && <button type="button" onClick={() => add(product)} aria-label={`${product.name} in winkelwagen`} className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-paper text-ink shadow-sm active:scale-90 lg:hidden" data-id="element-344" data-mp="1k1y4vgpjdpdx">
            <PlusIcon className="h-4 w-4" data-id="element-345" data-mp="1aeu4pqed9mks" />
          </button>}
      </div>

      <div className="flex flex-1 flex-col pt-4" data-id="element-346" data-mp="15epx6pvfs26p">
        <p className="text-xs text-muted" data-id="element-347" data-mp="15h2er1xa8aj8">{product.type}</p>
        <h3 className="mt-1 text-[15px] font-medium leading-snug text-ink" data-id="element-348" data-mp="1jdpcv80z16qq">
          <Link to={href} className="hover:underline hover:underline-offset-4" data-id="element-349" data-mp="1eg7do9cra5vx">
            {product.name}
          </Link>
        </h3>
        <div className="mt-auto flex items-center justify-between gap-3 pt-1.5" data-id="element-350" data-mp="1t40s3qq3v2cx">
          <p className="text-[15px] text-ink" data-id="element-351" data-mp="1o6ihlvwfwf2t">
            {product.priceFrom && <span className="text-muted" data-id="element-352" data-mp="224yvc6spz5ug">vanaf </span>}
            {formatPrice(product.price)}
          </p>
          {product.colors && product.colors.length > 1 && <ul className="flex items-center gap-1" aria-label={`${product.colors.length} kleuren`} data-id="element-353" data-mp="1y1k7ityjhl6g">
              {product.colors.map(c => <li key={c} className="h-3 w-3 rounded-full ring-1 ring-ink/10" style={{
            backgroundColor: colorSwatches[c]
          }} title={c} data-id="element-354" data-mp="faw3xhe2lbq" />)}
            </ul>}
        </div>
      </div>
    </article>;
}